import { create } from "zustand";

/**
 * Pilot access token — one shared token per office, stored locally and
 * attached to every API call. The backend is a no-op until
 * ``PERMITPILOT_ACCESS_TOKENS`` is configured, so dev setups never see
 * the gate.
 */

const STORAGE_KEY = "permitpilot.access_token.v1";

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setAccessToken(token: string): void {
  localStorage.setItem(STORAGE_KEY, token.trim());
}

/** Headers to merge into every API request. */
export function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

interface AuthGateStore {
  /** True once any API call came back 401 — AppShell shows the gate. */
  required: boolean;
  setRequired: (v: boolean) => void;
}

export const useAuthGate = create<AuthGateStore>((set) => ({
  required: false,
  setRequired: (v) => set({ required: v }),
}));

/** Call on every API response; flips the gate on 401. Returns true when
 * the response was a 401 (so callers can short-circuit). */
export function handleUnauthorized(res: Response): boolean {
  if (res.status !== 401) return false;
  useAuthGate.getState().setRequired(true);
  return true;
}
